<script lang="ts">
	import { toast } from '@Shelly/shelly-design-system';
	import { onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import { readable, writable, type Readable } from 'svelte/store';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import WebRtcInfo from './Debug/WebRTCInfo.svelte';
	import { computeStreamState } from '../helpers/camera-state-helpers';
	import { createThumbnailResolver } from '../helpers/encryption/recording-image-decryption';
	import { openWebRTCSession } from '../helpers/webrtc-session-manager';
	import { webrtcTrace } from '../helpers/webrtc-trace';
	import { NO_STREAM_ERROR, webrtcSessionKey } from '../helpers/webrtc-utils';
	import type { CloudImageSize } from '../types/cloud-recordings';
	import { PttUiState } from '../types/ptt-ui-state';
	import type {
		WebRTCPlayerSession,
		WebRTCPlayerState,
		WebRTCSessionLease,
		WebRTCSessionState,
	} from '../types/webrtc-session';

	export let deviceId: string = null;
	export let streamId: number = 0;
	export let showControls: boolean = true;
	export let objectFit: string = 'cover';
	/** Fill the parent cell (viewport-fit mosaics) instead of locking to 16:9. */
	export let fillContainer = false;

	/**
	 * Size of the latest-recording thumbnail used as the `<video>` poster, shown
	 * until the stream plays. Small players should ask for a small image: each
	 * size is a separate fetch and decryption.
	 */
	export let previewSize: CloudImageSize = 'large';

	// Whether to show debug info below the video
	export let showInfo: boolean = false;

	/**
	 * Bound out for parents that render the stream's state or drive talkback.
	 * Read it with `$session` and act on it with `session`; the player owns the
	 * lease, so joining and leaving stay in here.
	 */
	export let session: WebRTCPlayerSession = null;

	const defaultPoster = load_image_from('images/device_images/logo_white_S.png');

	let imagePoster = defaultPoster;

	const posterResolver = createThumbnailResolver((url) => (imagePoster = url || defaultPoster));

	let videoElement: HTMLVideoElement = null;
	let lease: WebRTCSessionLease | null = null;
	let sessionKey = '';
	// Latched by `stop()`. A stopped player is still mounted, so without this a
	// prop change would re-open the session behind the parent's back.
	let stopped = false;

	// The shared state of whichever session is held, read with `$sessionState`:
	// re-pointing the variable re-subscribes, so the lease is swapped in one
	// assignment. Placeholder until the first session opens.
	let sessionState: Readable<WebRTCSessionState> = readable(emptySessionState());

	// The readiness of this player's own <video>, which is what makes the state
	// below the player's rather than the session's.
	let playback = { playing: false, failed: false };
	let attachedElement: HTMLVideoElement | null = null;
	let attachedStream: MediaStream | null = null;

	// Bound out rather than derived, because a parent binds once while the
	// leases behind this come and go.
	const playerState = writable<WebRTCPlayerState>({ ...emptySessionState(), streamState: null });

	$: posterResolver.update({ deviceId, previewSize });
	// Which camera to play is a prop, so mounting and any later change to it are
	// the same thing: open the session it now points at.
	$: if (!stopped) openSession(deviceId, streamId);
	$: attachStream(videoElement, $sessionState.stream);
	// Without a session there is nothing to report, so the container shows
	// neither a spinner nor an error.
	$: playerState.set({
		...$sessionState,
		streamState: lease ? computeStreamState($sessionState, playback) : null,
	});
	// Re-read on every state change so the panel follows reconnects; a debug
	// affordance is the one place allowed to see the client itself.
	$: debugClient = showInfo && $sessionState ? (lease?.getClient() ?? null) : null;

	function emptySessionState(): WebRTCSessionState {
		return {
			stream: null,
			loading: true,
			error: NO_STREAM_ERROR,
			pttState: PttUiState.UNAVAILABLE,
		};
	}

	const closeSession = () => {
		lease?.release();
		lease = null;
		sessionKey = '';
		sessionState = readable(emptySessionState());
	};

	/**
	 * Joins the camera's shared session. Keyed the same way as the session
	 * manager, so re-opening the session already held — a channel-suffix change
	 * on the same camera, or `start()` on a player that never left — is a no-op
	 * rather than a renegotiation.
	 */
	const openSession = (device_id: string, stream_id: number) => {
		const key = device_id ? webrtcSessionKey(device_id, stream_id) : '';
		if (key === sessionKey) {
			return;
		}

		closeSession();
		if (!key) {
			return;
		}

		sessionKey = key;
		lease = openWebRTCSession(device_id, stream_id, {
			onTalkTimeout: handleTalkTimeout,
			onTalkFailed: handleTalkFailed,
		});
		// Replays the session's current state on subscribe, so a player joining
		// a live connection never flashes its loading state.
		sessionState = lease.state;
	};

	/**
	 * A playback failure is local to this `<video>`, so it is surfaced here
	 * instead of rebuilding the connection that the other players are using.
	 */
	const handlePlayError = (e: unknown) => {
		console.warn('[WebRTC Player] Video play error:', e);
		playback = { playing: false, failed: true };
	};

	/**
	 * Points the element at the shared stream. Guarded on the pair it last
	 * attached because this runs for every session state change, not only for a
	 * new stream, and re-attaching would restart the buffering it is reporting.
	 *
	 * The stream is owned by the session and shared with every other player of
	 * this camera, so it is only ever detached here — never stopped.
	 */
	const attachStream = (element: HTMLVideoElement, stream: MediaStream | null) => {
		if (element === attachedElement && stream === attachedStream) {
			return;
		}
		attachedElement = element;
		attachedStream = stream;
		playback = { playing: false, failed: false };

		if (!element) {
			return;
		}
		element.srcObject = stream;
		if (!stream) {
			return;
		}

		element.muted = true;
		element.onplaying = () => {
			webrtcTrace('Player', `${sessionKey} · video playing`);
			playback = { playing: true, failed: false };
		};
		element.oncanplay = () => {
			// These media callbacks fire asynchronously; the `bind:this` is nulled
			// when the component is torn down mid-load, so guard the whole chain.
			videoElement?.play()?.catch(handlePlayError);
		};
	};

	const handleTalkTimeout = () => {
		toast.open({
			type: 'warning',
			title: $format('camera_talk_confirmation_error', {
				default: 'Push to talk could not be confirmed by camera',
			}),
		});
	};

	const handleTalkFailed = () => {
		toast.open({
			type: 'danger',
			title: $format('camera_talk_channel_lost_error', {
				default: 'Push to talk was ended by the camera',
			}),
		});
	};

	/**
	 * The browser has no event for "the permission prompt is showing", but a
	 * `'prompt'` state means the next getUserMedia call will raise one. Cosmetic
	 * only — getUserMedia's promise stays the source of truth.
	 */
	const warnAboutMicrophonePrompt = async () => {
		if (!navigator.permissions?.query) {
			return;
		}
		try {
			const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
			if (status.state !== 'prompt') {
				return;
			}
			toast.open({
				type: 'warning',
				title: $format('camera_talk_microphone_permission', {
					default: 'Allow microphone access to use Push to talk',
				}),
			});
		} catch {
			/* some browsers don't accept 'microphone' as a queryable permission */
		}
	};

	/**
	 * Claims or releases talkback. The claim belongs to the shared session, so
	 * any player of this camera can take it and all of them see the resulting
	 * state; the claim itself stays with this player's lease.
	 */
	const requestTalk = async (desired: boolean): Promise<void> => {
		webrtcTrace('Player', `${sessionKey} · talk click desired=${desired}`, {
			pttState: $sessionState.pttState,
			joined: !!lease,
		});
		if (!lease) {
			return;
		}
		if (desired) {
			// Deliberately not awaited: the hint is cosmetic, and awaiting it
			// held the button in its idle state — and clickable again — for the
			// length of a permissions query.
			void warnAboutMicrophonePrompt();
		}

		try {
			await lease.requestTalk(desired);
		} catch (e) {
			console.warn('[WebRTC Player] requestTalk failed:', e);
			toast.open({
				type: 'danger',
				title: $format('camera_microphone_access_denied', {
					default: 'Microphone access denied',
				}),
			});
		}
	};

	// Assigned once: the methods read the current lease when called, so parents
	// bind a stable object instead of re-binding on every reconnect.
	session = {
		subscribe: playerState.subscribe,
		requestTalk,
		retry: () => lease?.retry(),
	};

	/**
	 * Re-joins the camera's session. Exposed for parents that keep this player
	 * mounted while hidden and called `stop()` on the way out.
	 */
	export function start() {
		stopped = false;
		openSession(deviceId, streamId);
	}

	/**
	 * Leaves the camera's session. The connection itself lingers and is only
	 * torn down once every other player has left too, so a screen switch reuses
	 * it; talkback claimed by this player is given up at once.
	 *
	 * Exposed for parents that keep this component mounted while hidden: the
	 * drawer/sidepanel slides it off-screen instead of unmounting, so
	 * `onDestroy` never fires on close.
	 */
	export function stop() {
		stopped = true;
		closeSession();
	}

	onDestroy(() => {
		closeSession();
		posterResolver.destroy();
		if (videoElement) {
			videoElement.srcObject = null;
		}
	});
</script>

<video
	poster={imagePoster}
	bind:this={videoElement}
	autoPlay
	playsInline
	muted
	controls={showControls && playback.playing}
	class:fill-container={fillContainer}
	style={`object-fit: ${objectFit};`}
/>
{#if showInfo}
	<div class="width-full">
		<WebRtcInfo {deviceId} webRTChandler={debugClient} streamMediaStream={$sessionState.stream} />
	</div>
{/if}

<style>
	video {
		display: block;
		width: 100%;
		aspect-ratio: 16 / 9;
		background-color: var(--color-black, #000);
	}

	video.fill-container {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		aspect-ratio: unset;
	}
</style>
